import { Resource, RequestErrorEvent } from "cesium";
import moment, { Moment } from "moment";
import { authenticatedAxios } from "lib/actions/fetcher";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";

type EndpointModel = {
  baseUrl: string;
  resourcePath: string;
  authQueryStringPart: string;
  expiresOn: Moment;
};

type ProjectEndpointCacheModel = Omit<EndpointModel, "resourcePath">;

/**
 * A {@link Resource} instance that encapsulates Azure Storage asset access.
 */
export class AzureStorageResource extends Resource {
  /** Details for requesting from the Azure Storage endpoint. */
  private _endpoint: EndpointModel;
  /** The primary {@link AzureStorageResource} from which clones are derived. */
  private _rootInstance: AzureStorageResource;
  /** To fetch and refetch endpoint info and auth tokens. */
  private readonly _getEndpointCallback: ReturnType<
    typeof AzureStorageResource.createGetEndpointCallback
  >;
  private _pendingGetEndpointCallback: Promise<EndpointModel>;

  private static _projectEndpointCache = new Map<
    number,
    ProjectEndpointCacheModel
  >();

  private constructor(
    endpoint: EndpointModel,
    getEndpointCallback: () => Promise<EndpointModel>,
  ) {
    let options: Resource.ConstructorOptions = {
      url: AzureStorageResource.createUrlFromEndpoint(endpoint),
      parseUrl: true,
      retryAttempts: 1,
      retryCallback: AzureStorageResource.refreshTokenRetryCallback,
    };
    super(options);

    this._endpoint = endpoint;
    this._getEndpointCallback = getEndpointCallback;
  }

  /** @inheritdoc */
  public override clone(result: Resource) {
    const root = this.getRootInstance();
    if (!result) {
      result = new AzureStorageResource(
        // We always want to use the root's information because it's the most up-to-date
        root._endpoint,
        root._getEndpointCallback,
      );
    }
    if (result instanceof AzureStorageResource) {
      result._rootInstance = root;
    }
    result = super.clone(result);
    return result;
  }

  private getRootInstance(): AzureStorageResource {
    return this._rootInstance ?? this;
  }

  private setEndpoint(value: EndpointModel) {
    this.url = AzureStorageResource.createUrlFromEndpoint(value);
    this._endpoint = value;
  }

  /**
   * @param resourcePath Absolute path to resource. ie. /model1/tileset.json
   * @param projectId Project for which to get endpoint information.
   */
  public static async fromUrl(resourcePath: string, projectId: number) {
    const normalizedPath = resourcePath.startsWith("/")
      ? resourcePath
      : "/" + resourcePath;
    const callback = AzureStorageResource.createGetEndpointCallback(
      projectId,
      normalizedPath,
    );
    const endpoint = await callback();
    return new AzureStorageResource(endpoint, callback);
  }

  private static createGetEndpointCallback(
    projectId: number,
    resourcePath: string,
  ) {
    return async function (invalidateCache = false) {
      const cached = AzureStorageResource._projectEndpointCache.get(projectId);
      if (cached) {
        if (
          !invalidateCache &&
          (!cached.expiresOn || cached.expiresOn.isAfter())
        ) {
          return {
            ...cached,
            resourcePath,
          };
        }
        AzureStorageResource._projectEndpointCache.delete(projectId);
      }
      const response = await authenticatedAxios
        .get<{
          baseUrl: string;
          authQueryStringPart: string;
          expiresOn: string;
        }>(endpointsAssets.projectCdn(projectId))
        .then((r) => r.data);
      const endpoint: EndpointModel = {
        baseUrl: response.baseUrl,
        resourcePath: resourcePath,
        authQueryStringPart: response.authQueryStringPart,
        expiresOn: moment(response.expiresOn),
      };
      AzureStorageResource._projectEndpointCache.set(projectId, endpoint);
      return endpoint;
    };
  }

  private static createUrlFromEndpoint(value: EndpointModel) {
    return value.baseUrl + value.resourcePath + "?" + value.authQueryStringPart;
  }

  private static refreshTokenRetryCallback(
    retryingResource: AzureStorageResource,
    error: RequestErrorEvent,
  ): Promise<boolean> {
    const root = retryingResource.getRootInstance();

    // Image is not available in worker threads, so this avoids
    // a ReferenceError
    const imageDefined = typeof Image !== "undefined";

    // We only want to retry in the case of invalid credentials (401) or image
    // requests(since Image failures can not provide a status code)
    if (
      !error ||
      (error.statusCode !== 401 &&
        !(imageDefined && (error as any).target instanceof Image))
    ) {
      return Promise.resolve(false);
    }

    // We use a shared pending promise for all derived assets, since they share
    // common endpoint info. If we're already requesting a new token for this
    // asset, we wait on the same promise.
    if (!root._pendingGetEndpointCallback) {
      root._pendingGetEndpointCallback = root
        ._getEndpointCallback(true)
        .then((newEndpoint) => {
          root.setEndpoint(newEndpoint);
          return newEndpoint;
        })
        .finally(() => {
          root._pendingGetEndpointCallback = undefined;
        });
    }

    return root._pendingGetEndpointCallback.then((newEndpoint) => {
      retryingResource.setEndpoint(newEndpoint);
      return true;
    });
  }
}
